import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getPlanVsActual } from '../../../services/timesheetService';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import timesheetStatusColors from '../../../utils/timesheetStatusColors';
import { translateTimesheetStatuses } from '../../../utils/timesheetStatusLabels';
import { statusReportLabels } from '../../../utils/reportLabels';
import { useTaFilterColumns } from './taFilterColumns';
import { useTaListFetch } from './useTaListFetch';

const Badge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $bg }) => $bg || '#9e9e9e15'};
  color: ${({ $color }) => $color || '#9e9e9e'};
`;

const Deviation = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${({ $sign }) => ($sign > 0 ? '#c2410c' : $sign < 0 ? '#0369a1' : 'inherit')};
`;

/** İşaretli sapma "+0:10" / "-1:05" / "0:00"; değer yoksa "-". */
export const formatDeviation = (minutes) => {
  if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) return '-';
  const n = Math.round(Number(minutes));
  const abs = Math.abs(n);
  const text = `${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
  if (n > 0) return `+${text}`;
  if (n < 0) return `-${text}`;
  return text;
};

/**
 * TR duvar saati (sunucu saat dilimsiz yazar: "2026-10-05T08:00:00"): "SS:dd"; satırın gününden başka güne düşüyorsa
 * (gece vardiyasının bitişi) "gg.AA SS:dd". Tarayıcı saat dilimine çevrilmez.
 */
export const wallTime = (value, rowDate) => {
  if (!value) return '-';
  const s = String(value);
  const tIdx = s.indexOf('T');
  if (tIdx < 0 || s.length < tIdx + 6) return '-';
  const time = s.substring(tIdx + 1, tIdx + 6);
  const day = s.substring(0, 10);
  const row = rowDate ? String(rowDate).substring(0, 10) : day;
  return day === row ? time : `${day.substring(8, 10)}.${day.substring(5, 7)} ${time}`;
};

/**
 * AKM POC §7.4 — Plan–Gerçekleşen Sapma: planlı her gün için plan başlangıç/bitiş, ilk giriş/son çıkış ve işaretli sapma
 * (giriş: + geç, − erken geliş; çıkış: − erken çıkış, + geç çıkış). Gelmeyen günde sapma yok, "Gelmedi" rozeti.
 */
export function usePlanVsActual() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const list = useTaListFetch(getPlanVsActual);
  const filterColumns = useTaFilterColumns(t);
  const statusLabels = useMemo(() => translateTimesheetStatuses(t), [t]);
  const { data } = list;

  // Dosyada sapma DAKİKA (tam sayı) yazılır — başlık "(Dk)" taşır; ekranda işaretli SS:dd.
  const exportLabels = useCallback(() => ({
    startDeviationMinutes: t('taReports.planVsActual.startDeviationMinutes'),
    endDeviationMinutes: t('taReports.planVsActual.endDeviationMinutes'),
    'planVsActual.absent': t('taReports.planVsActual.absent'),
    ...statusReportLabels(statusLabels),
  }), [t, statusLabels]);

  const columns = useMemo(() => {
    const clock = (name, label) => ({
      name, label,
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => wallTime(v, data[tableMeta.rowIndex]?.date),
      },
    });
    const deviation = (name, label) => ({
      name, label,
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => {
          if (data[tableMeta.rowIndex]?.isAbsent) return '-';
          return <Deviation $sign={Math.sign(Number(v) || 0)}>{formatDeviation(v)}</Deviation>;
        },
      },
    });
    return [
      { name: 'userId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'fullName', label: t('timesheet.employee'),
        options: {
          filter: true, sort: false, filterType: 'textField',
          filterOptions: { filterKey: 'userFullName[like]' },
        },
      },
      dateFilterColumn('date', t('timesheet.date'), lang, { sort: false }),
      {
        name: 'workHourName', label: t('timesheet.workHour'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      clock('plannedStart', t('taReports.planVsActual.plannedStart')),
      clock('plannedEnd', t('taReports.planVsActual.plannedEnd')),
      clock('firstEntry', t('taReports.planVsActual.firstEntry')),
      clock('lastExit', t('taReports.planVsActual.lastExit')),
      deviation('startDeviationMinutes', t('taReports.planVsActual.startDeviation')),
      deviation('endDeviationMinutes', t('taReports.planVsActual.endDeviation')),
      {
        name: 'status', label: t('timesheet.status'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v, tableMeta) => {
            if (data[tableMeta.rowIndex]?.isAbsent) {
              return <Badge $bg="#d32f2f15" $color="#d32f2f" data-absent="true">{t('taReports.planVsActual.absent')}</Badge>;
            }
            const colors = timesheetStatusColors[v] || { bg: '#9e9e9e15', text: '#9e9e9e' };
            return <Badge $bg={colors.bg} $color={colors.text}>{statusLabels[v] || v || '-'}</Badge>;
          },
        },
      },
      ...filterColumns,
    ];
  }, [t, lang, data, statusLabels, filterColumns]);

  return { ...list, columns, exportLabels };
}

export default usePlanVsActual;
